"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import Home from "../home/Home"
import RequestsCount from "../requests/RequestsCount"
import ReviewStatusDistribution from "./Charts/ReviewStatus"
import {
  FileText,
  Flag,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Info,
  Sun,
  Moon,
  Sunrise,
  Activity,
  Award,
  AlertTriangle,
  X,
  Building2,
} from "lucide-react"
import NoSessionAlert from "../ratings/components/NoSessionAlert"

interface RaterData {
  ratings: {
    [key: string]: { [key: string]: number | number[] }
  }
  stopDoing: string
  startDoing: string
  continueDoing: string
  type: "value" | "leadership" | "professional"
}

interface GroupedRating {
  rateeId: string
  raterId: string
  raterData: RaterData
  date: string
}

interface GroupedRatings {
  [rateeId: string]: GroupedRating[]
}

interface ReviewStatus {
  status: string
  count: number
}

interface SortedRating {
  rateeId: string
  ratings: GroupedRating[]
  finalOverallAverage: number
}

const AdminDashboard = observer(() => {
  const { api, store } = useAppContext()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [topRatedIndividual, setTopRatedIndividual] = useState<string | null>(null)
  const [lowRatedIndividual, setLowRatedIndividual] = useState<string | null>(null)
  const [openAboutDialog, setOpenAboutDialog] = useState(false)
  const [flaggedRequestsCount, setFlaggedRequestsCount] = useState<number>(0)
  const [sortedRatings, setSortedRatings] = useState<SortedRating[]>([])
  const [reviewStatusData, setReviewStatusData] = useState<ReviewStatus[]>([])
  const [allDepartmentStats, setAllDepartmentStats] = useState<
    Array<{
      name: string
      average: number
      count: number
      valuesAvg: number
      leadershipAvg: number
      professionalAvg: number
    }>
  >([],)
  const hasFetchedData = useRef(false)

  const getDepartmentName = (departmentId: string): string => {
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : ""
  }

  const getDisplayNameById = (userId: string) => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user ? user.displayName : undefined
  }

  const currentUserId = store.auth.meJson?.uid
  const displayName = currentUserId ? getDisplayNameById(currentUserId) : undefined

  const currentHour = new Date().getHours()
  let greeting
  let GreetingIcon

  if (currentHour < 12) {
    greeting = "Good Morning"
    GreetingIcon = Sunrise
  } else if (currentHour < 18) {
    greeting = "Good Afternoon"
    GreetingIcon = Sun
  } else {
    greeting = "Good Evening"
    GreetingIcon = Moon
  }

  const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
  const leadershipRatings = store.leadershipRating.all.map((rating) => rating.asJson)
  const templateRatings = store.templateRating.all.map((rating) => rating.asJson)

  useEffect(() => {
    if (hasFetchedData.current) return

    const fetchData = async () => {
      try {
        setLoading(true)
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const inProgressAssignment = store.ratingAssignments.all.find(
          (rate) => rate.asJson.status === "In Progress",
        )?.asJson

        if (inProgressAssignment && users.length && inProgressAssignment.description) {
          const uids = users.map((user) => user.uid)
          await Promise.all([
            api.valueRating.getAllNew(uids, inProgressAssignment.description),
            api.leadershipRating.getAll(uids[0], inProgressAssignment.description),
            api.templateRating.getAllWithDescription(inProgressAssignment.description),
          ])

          const groupedRatings: GroupedRatings = {}

          valueRatings.forEach((rating) => {
            const rateeId = rating.rateeId
            const raterEntries = Object.entries(rating.values)
            raterEntries.forEach(([raterId, raterData]) => {
              groupedRatings[rateeId] = groupedRatings[rateeId] || []
              groupedRatings[rateeId].push({
                rateeId,
                raterId,
                raterData: { ...(raterData as RaterData), type: "value" },
                date: rating.date.toString(),
              })
            })
          })

          leadershipRatings.forEach((rating) => {
            const rateeId = rating.rateeId
            const raterEntries = Object.entries(rating.criteria)
            raterEntries.forEach(([raterId, raterData]) => {
              groupedRatings[rateeId] = groupedRatings[rateeId] || []
              groupedRatings[rateeId].push({
                rateeId,
                raterId,
                raterData: { ...(raterData as RaterData), type: "leadership" },
                date: rating.date.toString(),
              })
            })
          })

          // include professional/template ratings as well
          templateRatings.forEach((rating) => {
            const rateeId = rating.rateeId
            const raterEntries = Object.entries(rating.values)
            raterEntries.forEach(([raterId, raterData]) => {
              groupedRatings[rateeId] = groupedRatings[rateeId] || []
              groupedRatings[rateeId].push({
                rateeId,
                raterId,
                raterData: { ...(raterData as RaterData), type: "professional" },
                date: rating.dateRequested?.toString() || new Date().toString(),
              })
            })
          })

          const sortedRatingsData: SortedRating[] = Object.entries(groupedRatings)
            .map(([rateeId, ratings]) => {
              const totalOverallAverage = ratings.reduce((total, rating) => {
                let totalRatings = 0
                let ratingCounter = 0
                Object.entries(rating.raterData.ratings).forEach(([valueName, statements]) => {
                  let totalValueRating = 0
                  let valueRatingCount = 0
                  Object.values(statements).forEach((ratingDetail) => {
                    if (Array.isArray(ratingDetail)) {
                      totalValueRating += ratingDetail.reduce((sum, current) => sum + current, 0)
                      valueRatingCount += ratingDetail.length
                    } else {
                      totalValueRating += ratingDetail as number
                      valueRatingCount += 1
                    }
                  })
                  const averageRating = valueRatingCount > 0 ? totalValueRating / valueRatingCount : 0
                  totalRatings += averageRating
                  ratingCounter += 1
                })
                const overallAverageRating = ratingCounter > 0 ? totalRatings / ratingCounter : 0
                return total + overallAverageRating
              }, 0)

              const finalOverallAverage = ratings.length > 0 ? totalOverallAverage / ratings.length : 0

              return { rateeId, ratings, finalOverallAverage }
            })
            .sort((a, b) => b.finalOverallAverage - a.finalOverallAverage)

          setSortedRatings(sortedRatingsData)

          if (sortedRatingsData.length > 0) {
            const topRatedUser = getDisplayNameById(sortedRatingsData[0].rateeId)
            setTopRatedIndividual(topRatedUser ?? "N/A")
          } else {
            setTopRatedIndividual("N/A")
          }

          const lowRatedRating = sortedRatingsData.find((rating) => rating.finalOverallAverage < 3)
          if (lowRatedRating) {
            const lowRatedUser = getDisplayNameById(lowRatedRating.rateeId)
            setLowRatedIndividual(lowRatedUser ?? "N/A")
          } else {
            setLowRatedIndividual("N/A")
          }

          // max: include professional/template ratings and compute per-department averages per category
          const departmentCategoryRatings: {
            [departmentName: string]: { values: number[]; leadership: number[]; professional: number[] }
          } = {}

          // for each rated user, compute per-category average and append to department buckets
          sortedRatingsData.forEach((ratingEntry) => {
            const user = store.user.all.find((u) => u.asJson.uid === ratingEntry.rateeId)?.asJson
            if (!user || !user.department) return
            const deptName = getDepartmentName(user.department)
            if (!deptName) return
            if (!departmentCategoryRatings[deptName]) {
              departmentCategoryRatings[deptName] = { values: [], leadership: [], professional: [] }
            }

            const ratings = ratingEntry.ratings

            const avgForType = (type: "value" | "leadership" | "professional") => {
              const filtered = ratings.filter((r) => r.raterData.type === type)
              if (filtered.length === 0) return 0
              const perRaterAverages = filtered.map((r) => {
                let total = 0
                let count = 0
                Object.values(r.raterData.ratings).forEach((statements) => {
                  const values = Object.values(statements).flat() as number[]
                  total += values.reduce((s, v) => s + v, 0)
                  count += values.length
                })
                return count > 0 ? total / count : 0
              })
              const sum = perRaterAverages.reduce((s, v) => s + v, 0)
              return perRaterAverages.length > 0 ? sum / perRaterAverages.length : 0
            }

            const vAvg = avgForType("value")
            const lAvg = avgForType("leadership")
            const pAvg = avgForType("professional")

            departmentCategoryRatings[deptName].values.push(vAvg)
            departmentCategoryRatings[deptName].leadership.push(lAvg)
            departmentCategoryRatings[deptName].professional.push(pAvg)
          })

          const allDepts = store.department.all.map((dept) => dept.asJson)
          const allDeptStats: Array<{
            name: string
            average: number
            count: number
            valuesAvg: number
            leadershipAvg: number
            professionalAvg: number
          }> = allDepts.map((dept) => {
            const deptName = dept.name || ""
            const bucket = departmentCategoryRatings[deptName] || { values: [], leadership: [], professional: [] }
            const valuesAvg = bucket.values.length > 0 ? bucket.values.reduce((s, v) => s + v, 0) / bucket.values.length : 0
            const leadershipAvg = bucket.leadership.length > 0 ? bucket.leadership.reduce((s, v) => s + v, 0) / bucket.leadership.length : 0
            const professionalAvg = bucket.professional.length > 0 ? bucket.professional.reduce((s, v) => s + v, 0) / bucket.professional.length : 0
            // overall average keeps previous behaviour (average of the three category averages)
            const averages = [valuesAvg, leadershipAvg, professionalAvg].filter((n) => n >= 0)
            const average = averages.length > 0 ? averages.reduce((s, v) => s + v, 0) / averages.length : 0
            const count = Math.max(bucket.values.length, bucket.leadership.length, bucket.professional.length)
            return { name: deptName, average, count, valuesAvg, leadershipAvg, professionalAvg }
          })
          setAllDepartmentStats(allDeptStats.sort((a, b) => b.average - a.average))

          const totalAssignments = Object.keys(inProgressAssignment.feedbackAssignments || {}).reduce(
            (sum, raterId) => sum + (inProgressAssignment.feedbackAssignments[raterId]?.length || 0),
            0,
          )

          const completedCount =
            valueRatings.filter((r) => r.status === "Completed").length +
            leadershipRatings.filter((r) => r.status === "Completed").length +
            templateRatings.filter((r) => r.status === "Completed").length

          const inProgressCount = totalAssignments - completedCount
          const notStartedCount = 0

          setReviewStatusData([
            { status: "Not Started", count: notStartedCount },
            { status: "In Progress", count: inProgressCount },
            { status: "Completed", count: completedCount },
          ])

          const flaggedCount =
            valueRatings.filter(
              (rating) =>
                rating.isflagged && rating.adminApproval !== "Approved for Re-rating" && rating.resubmission !== "Open",
            ).length +
            leadershipRatings.filter(
              (rating) =>
                rating.isflagged && rating.adminApproval !== "Approved for Re-rating" && rating.resubmission !== "Open",
            ).length +
            templateRatings.filter(
              (rating) =>
                rating.isflagged && rating.adminApproval !== "Approved for Re-rating" && rating.resubmission !== "Open",
            ).length
          setFlaggedRequestsCount(flaggedCount)

          hasFetchedData.current = true
        } else {
          console.log("Conditions not met for fetching ratings")
        }
      } catch (error) {
        console.error("Error fetching data:", error)
        setError("An error occurred while fetching data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleAboutClick = () => {
    setOpenAboutDialog(true)
  }

  const handleCloseAboutDialog = () => {
    setOpenAboutDialog(false)
  }

  const onViewTab = (tab: string) => {
    navigate(`/c/threesixty/home/overview?tab=${tab}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="space-y-4 sm:space-y-6 animate-fadeIn">
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-blue-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-pink-600/5"></div>
          <div className="relative text-center space-y-3 sm:space-y-4 lg:space-y-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="p-3 sm:p-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl sm:rounded-2xl shadow-lg">
                <GreetingIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                  {greeting}
                </h2>
                <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-700 mt-1">
                  {displayName || "Admin"}
                </p>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-4">
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">Welcome to the Admin Dashboard</p>
              <p className="text-sm sm:text-base lg:text-lg text-slate-500">
                Monitor performance, manage reviews, and track organizational insights
              </p>
            </div>

            <button
              onClick={handleAboutClick}
              className="inline-flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm hover:bg-white text-slate-700 hover:text-blue-600 font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl border border-white/50 hover:border-blue-200 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group text-sm sm:text-base"
            >
              <Info className="h-4 w-4 sm:h-5 sm:w-5" />
              Learn about 360° Feedback
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          {!store.ratingAssignments.all.some((r) => r.asJson.isActive) && (
            <NoSessionAlert onCreateSession={() => console.log("Open Create Session Modal")} />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          <div
            className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-blue-500/25"
            onClick={() => onViewTab("requests-review")}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                </div>
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <p className="text-blue-100 text-xs sm:text-sm font-medium uppercase tracking-wide">Review Requests</p>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  <RequestsCount />
                </div>
                <div className="flex items-center gap-2 text-blue-100 text-xs sm:text-sm">
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Pending Review</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div
            className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-emerald-500/25"
            onClick={() => onViewTab("top-rated")}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                </div>
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <p className="text-emerald-100 text-xs sm:text-sm font-medium uppercase tracking-wide">Top Rated</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">{topRatedIndividual}</p>
                <div className="flex items-center gap-2 text-emerald-100 text-xs sm:text-sm">
                  <Award className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Highest Performer</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div
            className="group relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-amber-500/25"
            onClick={() => onViewTab("low-rated")}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                  <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                </div>
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <p className="text-amber-100 text-xs sm:text-sm font-medium uppercase tracking-wide">Low Rated</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">{lowRatedIndividual}</p>
                <div className="flex items-center gap-2 text-amber-100 text-xs sm:text-sm">
                  <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Needs Attention</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div
            className="group relative overflow-hidden bg-gradient-to-br from-red-500 via-red-600 to-pink-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white cursor-pointer hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-red-500/25"
            onClick={() => onViewTab("flagged-requests")}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 lg:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl group-hover:bg-white/30 transition-colors duration-300">
                  <Flag className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                </div>
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
              </div>
              <div className="space-y-1 sm:space-y-2">
                <p className="text-red-100 text-xs sm:text-sm font-medium uppercase tracking-wide">Flagged Requests</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{flaggedRequestsCount}</p>
                <div className="flex items-center gap-2 text-red-100 text-xs sm:text-sm">
                  <Flag className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Requires Review</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>
        </div>

        {allDepartmentStats.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Building2 className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Department Performance</h3>
                <p className="text-sm text-slate-600">Average ratings by department</p>
                {/* V/L/P note removed per request */}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allDepartmentStats.map((dept) => (
                <div
                  key={dept.name}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-slate-800 text-lg">{dept.name}</h4>
                      <p className="text-sm text-slate-600">
                        {dept.count} employee{dept.count !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${dept.average >= 4 ? "bg-green-100 text-green-700" : dept.average >= 3 ? "bg-yellow-100 text-yellow-700" : dept.count === 0 ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-700"}`}>
                        {dept.count === 0 ? "No Data" : dept.average.toFixed(2)}
                      </div>
                      {/* V/L/P badges removed as requested; kept calculation logic but hidden */}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${dept.average >= 4 ? "bg-green-500" : dept.average >= 3 ? "bg-yellow-500" : dept.count === 0 ? "bg-gray-300" : "bg-red-500"}`}
                        style={{ width: `${Math.min((dept.average / 5) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-600">
                      {dept.count === 0
                        ? "No ratings yet"
                        : dept.average >= 4
                          ? "Excellent Performance"
                          : dept.average >= 3
                            ? "Good Performance"
                            : "Needs Improvement"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:gap-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500">
            <ReviewStatusDistribution data={reviewStatusData} />
          </div>
        </div>

        {openAboutDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">About 360° Feedback</h2>
                <button
                  onClick={handleCloseAboutDialog}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200 group"
                >
                  <X className="h-5 w-5 text-gray-600 group-hover:text-gray-800" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <Home />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default AdminDashboard
